import { useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@services/firebaseClient';
import authService from '@services/authService';
import logger from '@services/loggerService';

/**
 * A component that synchronizes Firebase auth state changes with the app's auth service.
 * It listens for sign-in, sign-out events from Firebase Auth
 * and dispatches a global 'authChange' event to update the UI.
 * This component does not render anything.
 */
const FirebaseAuthSync = () => {
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        const appUser = authService.getCurrentUser();

        if (user) {
          // User is signed in
          if (!appUser) {
            // We have a Firebase user but no app user — sync profile
            logger.info('FirebaseAuthSync: Signed in, syncing profile', { uid: user.uid });
            try {
              let profile = await authService.getProfile(user.uid);
              if (!profile) {
                const fallbackName = user.displayName?.split(' ') || [];
                profile = {
                  id: user.uid,
                  email: user.email || '',
                  firstName: fallbackName[0] || '',
                  lastName: fallbackName.slice(1).join(' ') || '',
                  avatarUrl: user.photoURL || undefined,
                  role: 'parent'
                };
              }

              const token = await user.getIdToken();
              localStorage.setItem('authToken', token);
              localStorage.setItem('user', JSON.stringify(profile));
              window.dispatchEvent(new Event('authChange'));
            } catch (syncError) {
              logger.error('FirebaseAuthSync: Error syncing user session', {}, syncError as Error);
              authService.logout();
            }
          } else if (appUser.id !== user.uid) {
            // User ID mismatch — re-sync
            logger.warn('FirebaseAuthSync: Session user ID mismatch, re-syncing', {
              appUserId: appUser.id,
              firebaseUid: user.uid
            });
            const profile = await authService.getProfile(user.uid);
            if (profile) {
              const token = await user.getIdToken();
              localStorage.setItem('authToken', token);
              localStorage.setItem('user', JSON.stringify(profile));
              window.dispatchEvent(new Event('authChange'));
            }
          }
        } else {
          // User is signed out
          if (appUser) {
            logger.info('FirebaseAuthSync: User signed out');
            authService.logout();
          }
        }
      } catch (err) {
        logger.error('FirebaseAuthSync: Unexpected error in auth state listener', {}, err as Error);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return null; // This component does not render anything
};

export default FirebaseAuthSync;
